import React, { useState } from 'react';
import { BookOpen, ExternalLink, Copy, Check } from 'lucide-react';
import './ApiDocumentation.css';

function ApiDocumentation() {
  const [copied, setCopied] = useState(null);
  const apiBaseUrl = window.location.origin + '/api';
  const docsUrl = window.location.origin + '/api/docs'; // Swagger docs at /api/docs

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const endpoints = [
    { method: 'GET', path: '/balances', description: 'Get all exchange and wallet balances' },
    { method: 'POST', path: '/transfers', description: 'Initiate a new transfer' },
    { method: 'GET', path: '/transfers/:id', description: 'Get transfer status and details' },
  ];

  return (
    <div className="api-docs-container">
      <div className="api-docs-header">
        <div className="api-docs-title">
          <BookOpen size={24} />
          <h2>API Documentation</h2>
        </div>
        <a 
          href={docsUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn-primary"
        >
          <span>Open Full Documentation</span>
          <ExternalLink size={16} />
        </a>
      </div>

      <div className="api-docs-section">
        <h3>Base URL</h3>
        <div className="api-url-box">
          <code>{apiBaseUrl}</code>
          <button 
            className="btn-copy"
            onClick={() => copyToClipboard(apiBaseUrl, 'baseUrl')}
          >
            {copied === 'baseUrl' ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
      </div>

      <div className="api-docs-section">
        <h3>Authentication</h3>
        <p>All API requests require authentication. Choose the method that fits your use case:</p>
        
        <div className="auth-method">
          <h4>1. API Key + Secret (Recommended for Scripts & Bots)</h4>
          <p>Use this for backend-to-backend integration, trading bots, and scripts. These keys do not expire.</p>
          <div className="code-block">
            <code>x-api-key: {'<your_api_key>'}</code>
            <code>x-api-secret: {'<your_api_secret>'}</code>
          </div>
          <p className="hint">Create API keys in the "API Keys" tab above. The account is automatically determined from your API key.</p>
        </div>

        <div className="auth-method">
          <h4>2. Bearer Token (For Custom Frontends)</h4>
          <p>Use this only if you are building a custom frontend application that authenticates users via Firebase Auth.</p>
          <div className="code-block">
            <code>Authorization: Bearer {'<firebase_id_token>'}</code>
          </div>
        </div>
      </div>

      <div className="api-docs-section">
        <h3>Transfer Operations</h3>
        <div className="endpoints-list">
          {endpoints.map((endpoint, index) => (
            <div key={index} className="endpoint-item">
              <span className={`method-badge ${endpoint.method.toLowerCase()}`}>
                {endpoint.method}
              </span>
              <code className="endpoint-path">{endpoint.path}</code>
              <span className="endpoint-desc">{endpoint.description}</span>
            </div>
          ))}
        </div>
        <p className="hint">
          View the full interactive documentation at{' '}
          <a href={docsUrl} target="_blank" rel="noopener noreferrer">
            {docsUrl}
          </a>
        </p>
      </div>

      <div className="api-docs-section">
        <h3>Transfer Request Schema</h3>
        <p>The <code>POST /transfers</code> endpoint accepts the following JSON body:</p>
        <div className="code-block schema">
          <code>&#123;</code>
          <code>  "requestId": "unique-request-id",     <span className="comment">// Required: unique idempotency key</span></code>
          <code>  "priority": "normal",                 <span className="comment">// Required: "normal" or "urgent"</span></code>
          <code>  "source": &#123;</code>
          <code>    "exchange": "binance",              <span className="comment">// Required: exchange name or wallet address</span></code>
          <code>    "asset": "USDT",                    <span className="comment">// Required: asset symbol</span></code>
          <code>    "amount": 100                       <span className="comment">// Required: amount to transfer</span></code>
          <code>  &#125;,</code>
          <code>  "destination": &#123;</code>
          <code>    "exchange": "0x...",                <span className="comment">// Required: exchange name or wallet address</span></code>
          <code>    "asset": "USDC",                    <span className="comment">// Optional: auto-swap if different from source</span></code>
          <code>    "chain": "arbitrum"                 <span className="comment">// Optional: defaults to arbitrum</span></code>
          <code>  &#125;</code>
          <code>&#125;</code>
        </div>
        <p className="hint">
          <strong>Tip:</strong> For wallet transfers, use the wallet address as the <code>exchange</code> field.
          The system auto-detects if it's an address or exchange name.
        </p>
      </div>

      <div className="api-docs-section">
        <h3>Example: Python Script</h3>
        <p>Here is a complete example of how to check balances and initiate a transfer using Python.</p>
        <div className="code-block example">
          <code className="comment">import requests</code>
          <code className="comment">import json</code>
          <code className="comment">import time</code>
          <code></code>
          <code>API_URL = "{apiBaseUrl}"</code>
          <code>HEADERS = &#123;</code>
          <code>    "x-api-key": "YOUR_API_KEY",</code>
          <code>    "x-api-secret": "YOUR_API_SECRET",</code>
          <code>    "Content-Type": "application/json"</code>
          <code>&#125;</code>
          <code></code>
          <code className="comment"># 1. Check Balances</code>
          <code>response = requests.get(f"&#123;API_URL&#125;/balances", headers=HEADERS)</code>
          <code>print("Balances:", json.dumps(response.json(), indent=2))</code>
          <code></code>
          <code className="comment"># 2. Initiate Transfer (Exchange to Wallet)</code>
          <code>payload = &#123;</code>
          <code>    "requestId": f"transfer-&#123;int(time.time())&#125;",</code>
          <code>    "priority": "normal",</code>
          <code>    "source": &#123;</code>
          <code>        "exchange": "binance",</code>
          <code>        "asset": "USDT",</code>
          <code>        "amount": 100</code>
          <code>    &#125;,</code>
          <code>    "destination": &#123;</code>
          <code>        "exchange": "0xYOUR_WALLET_ADDRESS",</code>
          <code>        "chain": "arbitrum"</code>
          <code>    &#125;</code>
          <code>&#125;</code>
          <code>response = requests.post(f"&#123;API_URL&#125;/transfers", headers=HEADERS, json=payload)</code>
          <code>print("Transfer Response:", json.dumps(response.json(), indent=2))</code>
        </div>
        <button 
          className="btn-copy-block"
          onClick={() => copyToClipboard(
`import requests
import json
import time

API_URL = "${apiBaseUrl}"
HEADERS = {
    "x-api-key": "YOUR_API_KEY",
    "x-api-secret": "YOUR_API_SECRET",
    "Content-Type": "application/json"
}

# 1. Check Balances
response = requests.get(f"{API_URL}/balances", headers=HEADERS)
print("Balances:", json.dumps(response.json(), indent=2))

# 2. Initiate Transfer (Exchange to Wallet)
payload = {
    "requestId": f"transfer-{int(time.time())}",
    "priority": "normal",
    "source": {
        "exchange": "binance",
        "asset": "USDT",
        "amount": 100
    },
    "destination": {
        "exchange": "0xYOUR_WALLET_ADDRESS",  # Wallet address or exchange name
        "chain": "arbitrum"
    }
}
response = requests.post(f"{API_URL}/transfers", headers=HEADERS, json=payload)
print("Transfer Response:", json.dumps(response.json(), indent=2))`,
            'python-example'
          )}
        >
          {copied === 'python-example' ? <Check size={14} /> : <Copy size={14} />}
          <span>{copied === 'python-example' ? 'Copied!' : 'Copy Python Code'}</span>
        </button>
      </div>

      <div className="api-docs-section">
        <h3>Example: Node.js Script</h3>
        <p>Here is a complete example using Node.js and axios.</p>
        <div className="code-block example">
          <code className="comment">const axios = require('axios');</code>
          <code></code>
          <code>const API_URL = "{apiBaseUrl}";</code>
          <code>const HEADERS = &#123;</code>
          <code>  "x-api-key": "YOUR_API_KEY",</code>
          <code>  "x-api-secret": "YOUR_API_SECRET",</code>
          <code>  "Content-Type": "application/json"</code>
          <code>&#125;;</code>
          <code></code>
          <code className="comment">// Initiate Transfer</code>
          <code>async function transfer() &#123;</code>
          <code>  const payload = &#123;</code>
          <code>    requestId: `transfer-$&#123;Date.now()&#125;`,</code>
          <code>    priority: "normal",</code>
          <code>    source: &#123;</code>
          <code>      exchange: "binance",</code>
          <code>      asset: "USDT",</code>
          <code>      amount: 100</code>
          <code>    &#125;,</code>
          <code>    destination: &#123;</code>
          <code>      exchange: "0xYOUR_WALLET_ADDRESS",</code>
          <code>      chain: "arbitrum"</code>
          <code>    &#125;</code>
          <code>  &#125;;</code>
          <code></code>
          <code>  const res = await axios.post(`$&#123;API_URL&#125;/transfers`, payload, &#123; headers: HEADERS &#125;);</code>
          <code>  console.log("Transfer ID:", res.data.id);</code>
          <code>  console.log("Status:", res.data.status);</code>
          <code>&#125;</code>
          <code></code>
          <code>transfer();</code>
        </div>
        <button 
          className="btn-copy-block"
          onClick={() => copyToClipboard(
`const axios = require('axios');

const API_URL = "${apiBaseUrl}";
const HEADERS = {
  "x-api-key": "YOUR_API_KEY",
  "x-api-secret": "YOUR_API_SECRET",
  "Content-Type": "application/json"
};

// Initiate Transfer
async function transfer() {
  const payload = {
    requestId: \`transfer-\${Date.now()}\`,
    priority: "normal",
    source: {
      exchange: "binance",
      asset: "USDT",
      amount: 100
    },
    destination: {
      exchange: "0xYOUR_WALLET_ADDRESS",
      chain: "arbitrum"
    }
  };

  const res = await axios.post(\`\${API_URL}/transfers\`, payload, { headers: HEADERS });
  console.log("Transfer ID:", res.data.id);
  console.log("Status:", res.data.status);
}

transfer();`,
            'nodejs-example'
          )}
        >
          {copied === 'nodejs-example' ? <Check size={14} /> : <Copy size={14} />}
          <span>{copied === 'nodejs-example' ? 'Copied!' : 'Copy Node.js Code'}</span>
        </button>
      </div>

      <div className="api-docs-section">
        <h3>Example: Check Transfer Status</h3>
        <div className="code-block example">
          <code className="comment"># Get status of a specific transfer</code>
          <code>curl -X GET "{apiBaseUrl}/transfers/YOUR_TRANSFER_ID" \</code>
          <code>  -H "x-api-key: YOUR_API_KEY" \</code>
          <code>  -H "x-api-secret: YOUR_API_SECRET"</code>
        </div>
        <button 
          className="btn-copy-block"
          onClick={() => copyToClipboard(
            `curl -X GET "${apiBaseUrl}/transfers/YOUR_TRANSFER_ID" \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -H "x-api-secret: YOUR_API_SECRET"`,
            'status-example'
          )}
        >
          {copied === 'status-example' ? <Check size={14} /> : <Copy size={14} />}
          <span>{copied === 'status-example' ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
    </div>
  );
}

export default ApiDocumentation;
